// TEMPORARY — delete before commit
import { useState, useEffect } from "react";
import confetti from "canvas-confetti";
import { Link } from "react-router-dom";
import { Check, ArrowLeft, ArrowRight, Ticket, User, Download, Calendar, Video, HelpCircle, Shield, Copy, QrCode, AlertTriangle, ExternalLink } from "lucide-react";
import { BuyerData } from "@/components/checkout/CheckoutStepBuyer";
import { toast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { DocumentInput } from "@/components/DocumentInput";
import { useTranslation } from "react-i18next";

const MOCK_STEPS = ["Comprador", "Participantes", "Confirmação"];

const MOCK_EVENT = {
  title: "Teste Evento Online",
  date: "02 de junho de 2026 · 15:00 — 19:00",
  venue: "Evento Online",
  coverImage: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200&h=200&fit=crop",
};

const MOCK_BUYER: BuyerData = {
  fullName: "João da Silva",
  email: "joao@exemplo.com.br",
  birthDate: "1990-05-15",
  cpf: "529.982.247-25",
  companyName: "",
  phone: "(11) 99999-9999",
  cep: "01310-100",
  street: "Av. Paulista",
  addressNumber: "1000",
  complement: "Apto 42",
  neighborhood: "Bela Vista",
  city: "São Paulo",
  state: "SP",
};

const MOCK_ATTENDEE = {
  name: "João da Silva",
  cpf: "529.982.247-25",
  email: "joao@exemplo.com.br",
  phone: "(11) 99999-9999",
};

// Mock questions — universal (order-level) shown once above all tickets
const MOCK_ORDER_QUESTIONS = [
  { id: "oq1", question: "Como você ficou sabendo do evento?", field_type: "select", is_required: true, options: ["Instagram", "Amigos", "Google", "E-mail", "Outro"] },
  { id: "oq2", question: "Observações gerais", field_type: "textarea", is_required: false, options: [] },
];

// Mock questions — per-attendee (shown inside each ticket card)
const MOCK_ATTENDEE_QUESTIONS = [
  { id: "aq1", question: "Tamanho da camiseta", field_type: "radio", is_required: true, options: ["PP", "P", "M", "G", "GG"] },
  { id: "aq2", question: "Restrições alimentares", field_type: "checkbox", is_required: false, options: ["Vegetariano", "Vegano", "Sem glúten", "Sem lactose", "Nenhuma"] },
  { id: "aq3", question: "Data de nascimento do participante", field_type: "date", is_required: false, options: [] },
  { id: "aq4", question: "Código de desconto estudantil", field_type: "text", is_required: false, options: [] },
  { id: "aq5", question: "Biografia curta (para crachá)", field_type: "textarea", is_required: false, options: [] },
];

// ----- shared components -----

function FieldLabel({ label, required }: { label: string; required?: boolean }) {
  return (
    <label className="text-xs font-semibold text-foreground">
      {label}{required && <span className="text-primary ml-0.5">*</span>}
    </label>
  );
}

function QuestionField({ q, value, onChange }: { q: any; value: string; onChange: (v: string) => void }) {
  switch (q.field_type) {
    case "textarea":
      return <Textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder="Sua resposta..." rows={2} maxLength={1000} />;
    case "select":
      return (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
          <SelectContent>
            {q.options.map((opt: string) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    case "radio":
      return (
        <RadioGroup value={value} onValueChange={onChange} className="space-y-1.5 pt-0.5">
          {q.options.map((opt: string) => (
            <div key={opt} className="flex items-center gap-2">
              <RadioGroupItem value={opt} id={`${q.id}-${opt}`} />
              <Label htmlFor={`${q.id}-${opt}`} className="text-sm font-normal cursor-pointer">{opt}</Label>
            </div>
          ))}
        </RadioGroup>
      );
    case "checkbox": {
      const selected: string[] = value ? value.split("|") : [];
      return (
        <div className="space-y-1.5 pt-0.5">
          {q.options.map((opt: string) => {
            const checked = selected.includes(opt);
            return (
              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  className="rounded"
                  checked={checked}
                  onCheckedChange={(c) => {
                    const next = c ? [...selected, opt] : selected.filter((s) => s !== opt);
                    onChange(next.join("|"));
                  }}
                />
                <span className="text-sm font-normal">{opt}</span>
              </label>
            );
          })}
        </div>
      );
    }
    case "date":
      return <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} />;
    default:
      return <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Sua resposta..." maxLength={500} />;
  }
}

// ----- Sidebar -----
function Sidebar() {
  return (
    <div className="w-full lg:w-[296px] shrink-0">
      <aside className="rounded-xl border border-border bg-card p-[22px] flex flex-col gap-[18px] sticky top-6">
        <h3 className="font-display text-[15px] font-bold text-foreground m-0">Resumo do pedido</h3>
        <div className="flex gap-3 items-start">
          <img src={MOCK_EVENT.coverImage} alt={MOCK_EVENT.title} className="w-[52px] h-[52px] rounded-[10px] object-cover shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[13px] leading-[1.35] text-foreground mb-1">{MOCK_EVENT.title}</p>
            <div className="text-[11px] text-muted-foreground leading-[1.6]">
              02 de junho de 2026<br />15:00 — 19:00<br />Evento Online
            </div>
          </div>
        </div>
        <div className="border-t border-border pt-[14px]">
          <div className="flex justify-between text-[13px]">
            <span><span className="font-medium">Normal</span><span className="text-muted-foreground ml-1.5">× 1</span></span>
            <span className="font-semibold">Gratuito</span>
          </div>
        </div>
        <div className="border-t border-border pt-[14px] flex flex-col gap-2">
          <div className="flex justify-between text-[12px]"><span className="text-muted-foreground">Subtotal</span><span>R$ 0,00</span></div>
          <div className="flex justify-between text-[12px]"><span className="text-muted-foreground">Taxa de serviço</span><span>R$ 0,00</span></div>
          <div className="flex justify-between font-bold text-[15px] pt-[10px] border-t border-border">
            <span>Total</span><span className="text-primary">Gratuito</span>
          </div>
        </div>
        <div className="border-t border-border pt-[2px] flex flex-col gap-2.5">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Pagamento 100% seguro e criptografado
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {["PIX", "VISA", "MASTER", "ELO", "BOLETO"].map((m) => (
              <span key={m} className="text-[9px] font-bold tracking-wide text-muted-foreground border border-border rounded-[5px] px-[7px] py-[3px]">{m}</span>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

// ----- Step 0: Comprador -----
function StepComprador({ buyerData, setBuyerData, onNext }: { buyerData: BuyerData; setBuyerData: React.Dispatch<React.SetStateAction<BuyerData>>; onNext: () => void }) {
  const data = buyerData;
  const setData = setBuyerData;
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-[22px] font-bold tracking-tight text-foreground">Dados do comprador</h2>
          <p className="text-[13px] text-muted-foreground mt-1">Usaremos essas informações para emitir e enviar os seus ingressos.</p>
        </div>
        <button onClick={() => setData(MOCK_BUYER)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] border border-border bg-transparent text-xs font-semibold text-foreground cursor-pointer whitespace-nowrap shrink-0 hover:bg-muted transition-colors">
          <User className="h-3 w-3" /> Usar meus dados
        </button>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div className="flex flex-col gap-1">
          <FieldLabel label="Nome completo" required />
          <Input value={data.fullName} onChange={(e) => setData((p) => ({ ...p, fullName: e.target.value }))} placeholder="Seu nome completo" />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel label="E-mail" required />
          <Input value={data.email} onChange={(e) => setData((p) => ({ ...p, email: e.target.value }))} placeholder="seuemail@exemplo.com" />
          <span className="text-[11px] text-muted-foreground">Os ingressos serão enviados para este endereço.</span>
        </div>
        <div className="flex flex-col gap-1 max-w-xs">
          <FieldLabel label="Data de nascimento" required />
          <Input type="date" value={data.birthDate} onChange={(e) => setData((p) => ({ ...p, birthDate: e.target.value }))} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <FieldLabel label="Telefone" required />
            <Input value={data.phone} onChange={(e) => setData((p) => ({ ...p, phone: e.target.value }))} placeholder="(00) 00000-0000" />
          </div>
          <div className="flex flex-col gap-1">
            <DocumentInput
              value={data.cpf}
              onChange={(val) => setData((p) => ({ ...p, cpf: val }))}
              label="CPF / CNPJ *"
              placeholder="000.000.000-00"
              required
            />
            <span className="text-[11px] text-muted-foreground">Obrigatório para emissão do ingresso.</span>
          </div>
        </div>

        {data.cpf.replace(/\D/g, "").length === 14 && (
          <div className="flex flex-col gap-1">
            <FieldLabel label="Razão Social" required />
            <Input
              value={data.companyName}
              onChange={(e) => setData((p) => ({ ...p, companyName: e.target.value }))}
              placeholder="Razão Social da empresa"
            />
          </div>
        )}
        {/* Separator and Address Header */}
        <div className="border-t border-border/60 my-4 pt-4">
          <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">Endereço de Cobrança</h3>
        </div>

        <div className="flex flex-col gap-1 max-w-xs">
          <FieldLabel label="CEP" required />
          <Input value={data.cep} onChange={(e) => setData((p) => ({ ...p, cep: e.target.value }))} placeholder="00000-000" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px] gap-4">
          <div className="flex flex-col gap-1"><FieldLabel label="Av./Rua" required /><Input value={data.street} onChange={(e) => setData((p) => ({ ...p, street: e.target.value }))} placeholder="Nome da rua" /></div>
          <div className="flex flex-col gap-1"><FieldLabel label="Número" required /><Input value={data.addressNumber} onChange={(e) => setData((p) => ({ ...p, addressNumber: e.target.value }))} placeholder="123" /></div>
          <div className="flex flex-col gap-1"><FieldLabel label="Compl." /><Input value={data.complement} onChange={(e) => setData((p) => ({ ...p, complement: e.target.value }))} placeholder="Apto..." /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1"><FieldLabel label="Bairro" required /><Input value={data.neighborhood} onChange={(e) => setData((p) => ({ ...p, neighborhood: e.target.value }))} placeholder="Bairro" /></div>
          <div className="flex flex-col gap-1"><FieldLabel label="Cidade" required /><Input value={data.city} onChange={(e) => setData((p) => ({ ...p, city: e.target.value }))} placeholder="Cidade" /></div>
          <div className="flex flex-col gap-1"><FieldLabel label="Estado" required /><Input value={data.state} onChange={(e) => setData((p) => ({ ...p, state: e.target.value }))} placeholder="SP" /></div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        <Link to="#" className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar ao carrinho
        </Link>
        <button onClick={onNext} className="inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-primary text-white border-none text-[15px] font-semibold cursor-pointer shadow-sm shadow-black/15 hover:opacity-90 transition-opacity">
          Continuar <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ----- Step 1: Participantes -----
function StepParticipantes({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const [att, setAtt] = useState(MOCK_ATTENDEE);
  const [orderAnswers, setOrderAnswers] = useState<Record<string, string>>({});
  const [attAnswers, setAttAnswers] = useState<Record<string, string>>({});

  const fillFromBuyer = () => {
    setAtt(MOCK_ATTENDEE);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-[22px] font-bold tracking-tight text-foreground">Participantes</h2>
          <p className="text-[13px] text-muted-foreground mt-1">Informe os dados de quem vai usar o ingresso.</p>
        </div>
        <button onClick={fillFromBuyer} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] border border-border bg-transparent text-xs font-semibold text-foreground cursor-pointer whitespace-nowrap shrink-0 hover:bg-muted transition-colors">
          <User className="h-3 w-3" /> Usar meus dados
        </button>
      </div>

      {/* Universal / order-level questions */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="px-5 py-3 border-b border-border" style={{ backgroundColor: '#f0f0f0', boxShadow: 'inset 3px 0 0 hsl(var(--primary))' }}>
          <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Perguntas do evento</p>
        </div>
        <div className="p-5 flex flex-col gap-4">
          {MOCK_ORDER_QUESTIONS.map((q) => (
            <div key={q.id} className="flex flex-col gap-1.5">
              <FieldLabel label={q.question} required={q.is_required} />
              <QuestionField
                q={q}
                value={orderAnswers[q.id] || ""}
                onChange={(v) => setOrderAnswers((p) => ({ ...p, [q.id]: v }))}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Ticket card */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {/* Ticket header */}
        <div className="px-5 py-[13px] border-b border-border flex items-center gap-3" style={{ backgroundColor: '#f0f0f0', boxShadow: 'inset 3px 0 0 hsl(var(--primary))' }}>
          <div>
            <div className="text-[13px] font-bold">Ingresso 1 <span className="font-normal text-muted-foreground">— Normal</span></div>
            <div className="text-[11px] text-muted-foreground">Gratuito</div>
          </div>
        </div>

        {/* Form body */}
        <div className="p-5 flex flex-col gap-4">
          {/* Nome completo */}
          <div className="flex flex-col gap-1">
            <FieldLabel label="Nome completo do participante" required />
            <Input value={att.name} onChange={(e) => setAtt((p) => ({ ...p, name: e.target.value }))} placeholder="Nome completo" />
          </div>

          {/* CPF + Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <FieldLabel label="CPF" required />
              <Input value={att.cpf} onChange={(e) => setAtt((p) => ({ ...p, cpf: e.target.value }))} placeholder="000.000.000-00" inputMode="numeric" />
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel label="E-mail do participante" />
              <Input type="email" value={att.email} onChange={(e) => setAtt((p) => ({ ...p, email: e.target.value }))} placeholder="email@exemplo.com" />
            </div>
          </div>

          {/* Celular */}
          <div className="flex flex-col gap-1 max-w-xs">
            <FieldLabel label="Celular" required />
            <Input value={att.phone} onChange={(e) => setAtt((p) => ({ ...p, phone: e.target.value }))} placeholder="(00) 00000-0000" inputMode="tel" />
          </div>

          {/* Per-attendee questions */}
          {MOCK_ATTENDEE_QUESTIONS.length > 0 && (
            <div className="pt-3 border-t border-border flex flex-col gap-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide -mb-1">Perguntas adicionais</p>
              {MOCK_ATTENDEE_QUESTIONS.map((q) => (
                <div key={q.id} className="flex flex-col gap-1.5">
                  <FieldLabel label={q.question} required={q.is_required} />
                  <QuestionField
                    q={q}
                    value={attAnswers[q.id] || ""}
                    onChange={(v) => setAttAnswers((p) => ({ ...p, [q.id]: v }))}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 h-11 px-5 rounded-xl border border-border bg-transparent text-[14px] font-medium text-foreground cursor-pointer hover:bg-muted transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar
        </button>
        <button onClick={onNext} className="inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-primary text-white border-none text-[15px] font-semibold cursor-pointer shadow-sm shadow-black/15 hover:opacity-90 transition-opacity">
          Confirmar pedido <Check className="h-4 w-4 stroke-[2.5]" />
        </button>
      </div>
    </div>
  );
}

// ----- QR mock (large) -----
const QR_CELLS = [
  [1,1,1,1,1,1,1,0,1,0,1,1,1,1,1,1,1],
  [1,0,0,0,0,0,1,0,0,0,1,0,0,0,0,0,1],
  [1,0,1,1,1,0,1,0,1,1,1,0,1,1,1,0,1],
  [1,0,1,1,1,0,1,1,0,1,0,0,1,1,1,0,1],
  [1,0,1,1,1,0,1,0,1,0,1,0,1,1,1,0,1],
  [1,0,0,0,0,0,1,1,0,1,1,0,0,0,0,0,1],
  [1,1,1,1,1,1,1,0,1,0,1,1,1,1,1,1,1],
  [0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0],
  [1,0,1,1,0,1,1,1,0,1,1,0,1,1,0,1,1],
  [0,1,0,0,1,0,0,1,0,1,0,1,0,0,1,0,1],
  [1,1,0,1,1,0,1,0,1,0,1,0,1,1,1,1,0],
  [0,0,1,0,0,1,0,1,0,1,0,1,0,0,0,1,1],
  [1,0,1,1,1,0,1,1,1,0,1,1,1,0,1,1,0],
  [1,0,1,1,1,0,1,0,0,1,0,0,0,1,1,0,1],
  [1,0,1,1,1,0,1,0,1,0,1,1,1,0,1,1,1],
  [1,0,0,0,0,0,1,1,0,1,0,1,0,0,0,0,0],
  [1,1,1,1,1,1,1,0,1,0,1,0,1,1,0,1,1],
];

function QRMock({ size = 180 }: { size?: number }) {
  const n = QR_CELLS.length;
  const cell = size / n;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block", borderRadius: 10, background: "white", padding: cell * 0.6, flexShrink: 0 }}>
      {QR_CELLS.flatMap((row, r) =>
        row.map((v, c) =>
          v ? <rect key={`${r}-${c}`} x={c * cell} y={r * cell} width={cell * 0.88} height={cell * 0.88} rx={cell * 0.18} fill="#111" /> : null
        )
      )}
    </svg>
  );
}

// ── Icon shortcuts ──
const IconCopy = () => <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>;

// ── Animated SVGs for StepConfirmacao ──
function AnimatedPaperPlane() {
  return (
    <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
      <svg viewBox="0 0 80 80" className="w-16 h-16">
        <style>{`
          @keyframes openFlap {
            0%, 15% { transform: scaleY(1); }
            30%, 85% { transform: scaleY(-1); }
            95%, 100% { transform: scaleY(1); }
          }
          @keyframes launchPlane {
            0%, 25% { transform: translate(-5px, 8px) scale(0.6) rotate(15deg); opacity: 0; }
            40% { opacity: 1; }
            75% { transform: translate(25px, -25px) scale(1.1) rotate(-10deg); opacity: 0; }
            80%, 100% { opacity: 0; }
          }
          @keyframes floatEnvelope {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-2px); }
          }
          @keyframes sparkle {
            0%, 100% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1); opacity: 1; }
          }
          .flap-anim {
            transform-origin: 40px 48px;
            animation: openFlap 4s infinite ease-in-out;
          }
          .plane-anim {
            transform-origin: 40px 45px;
            animation: launchPlane 4s infinite ease-in-out;
          }
          .envelope-group {
            animation: floatEnvelope 4s infinite ease-in-out;
          }
        `}</style>
        <defs>
          <linearGradient id="brandOrange" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="100%" stopColor="#ff826e" />
          </linearGradient>
          <linearGradient id="envelopeBg" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ffebe8" />
            <stop offset="100%" stopColor="#ffd8d3" />
          </linearGradient>
        </defs>

        {/* Sparkles */}
        <g transform="translate(58, 18)">
          <path d="M 0,-3 Q 0,0 3,0 Q 0,0 0,3 Q 0,0 -3,0 Q 0,0 0,-3" fill="hsl(var(--primary))" className="scale-0" style={{ animation: "sparkle 2s infinite ease-in-out" }} />
        </g>
        <g transform="translate(18, 28)">
          <path d="M 0,-2.5 Q 0,0 2.5,0 Q 0,0 0,2.5 Q 0,0 -2.5,0 Q 0,0 0,-2.5" fill="#ff826e" className="scale-0" style={{ animation: "sparkle 2.5s infinite ease-in-out 0.5s" }} />
        </g>

        {/* Main Envelope Group */}
        <g className="envelope-group" transform="translate(0, 4)">
          {/* Envelope Back */}
          <rect x="20" y="38" width="40" height="26" rx="4" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" />
          
          {/* Letter Paper peaking out */}
          <rect x="24" y="30" width="32" height="20" rx="2" fill="white" stroke="#ffe0db" strokeWidth="1.2">
            <line x1="28" y1="36" x2="52" y2="36" stroke="#ffd8d3" strokeWidth="1" />
            <line x1="28" y1="40" x2="48" y2="40" stroke="#ffd8d3" strokeWidth="1" />
            <line x1="28" y1="44" x2="44" y2="44" stroke="#ffd8d3" strokeWidth="1" />
          </rect>

          {/* Flying Paper Plane */}
          <g className="plane-anim">
            <path d="M 40,40 L 48,28 L 32,34 Z" fill="url(#brandOrange)" />
            <path d="M 40,40 L 48,28 L 39,36 Z" fill="#d33c20" />
            <path d="M 39,36 L 40,40 L 41,36 Z" fill="#a72812" />
          </g>

          {/* Envelope Front Flaps */}
          <path d="M 20,38 L 40,50 L 20,64 Z" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M 60,38 L 40,50 L 60,64 Z" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M 20,64 L 40,48 L 60,64 Z" fill="#fff5f4" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" />
          <path className="flap-anim" d="M 20,38 L 40,48 L 60,38 Z" fill="#fff5f4" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" />
        </g>
      </svg>
    </div>
  );
}

function AnimatedTransmission() {
  return (
    <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
      <svg viewBox="0 0 80 80" className="w-16 h-16">
        <style>{`
          @keyframes floatMobile {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-2px); }
          }
          @keyframes floatHeart {
            0% { transform: translate(0, 0) scale(0.5); opacity: 0; }
            15% { opacity: 0.8; }
            80% { opacity: 0.8; }
            100% { transform: translate(var(--x-dir), -35px) scale(1); opacity: 0; }
          }
          @keyframes pulsePlay {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.15); }
          }
          .phone-container {
            animation: floatMobile 4s infinite ease-in-out;
            transform-origin: 40px 40px;
          }
          .heart {
            transform-origin: 40px 45px;
            animation: floatHeart 3s infinite linear;
          }
          .play-btn {
            transform-origin: 40px 40px;
            animation: pulsePlay 2s infinite ease-in-out;
          }
        `}</style>
        <defs>
          <linearGradient id="phoneScreenGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fff5f4" />
            <stop offset="100%" stopColor="#fff" />
          </linearGradient>
          <linearGradient id="primaryOrange" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="100%" stopColor="#ff826e" />
          </linearGradient>
        </defs>

        {/* Floating Reactions/Hearts */}
        <g className="heart" style={{ "--x-dir": "12px", animationDelay: "0s" } as any} transform="translate(40, 42)">
          <path d="M 0,0 C -3,-3 -6,-1 -6,2 C -6,5 0,9 0,9 C 0,9 6,5 6,2 C 6,-1 3,-3 0,0 Z" fill="hsl(var(--primary))" />
        </g>
        <g className="heart" style={{ "--x-dir": "-15px", animationDelay: "1s" } as any} transform="translate(40, 42)">
          <path d="M 0,0 C -2.5,-2.5 -5,-1 -5,1.5 C -5,4 0,7 0,7 C 0,7 5,4 5,1.5 C 5,-1 2.5,-2.5 0,0 Z" fill="#ff826e" />
        </g>
        <g className="heart" style={{ "--x-dir": "5px", animationDelay: "2s" } as any} transform="translate(40, 42)">
          <path d="M 0,0 C -2,-2 -4,-0.8 -4,1.2 C -4,3.2 0,5.6 0,5.6 C 0,5.6 4,3.2 4,1.2 C 4,-0.8 2,-2 0,0 Z" fill="#ffbda8" />
        </g>

        {/* Mobile Phone Screen Group */}
        <g className="phone-container" transform="translate(0, 2)">
          {/* Phone Shell */}
          <rect x="26" y="16" width="28" height="48" rx="5" fill="white" stroke="hsl(var(--primary))" strokeWidth="2" />
          {/* Phone Screen Area */}
          <rect x="29" y="20" width="22" height="40" rx="2" fill="url(#phoneScreenGrad)" />
          {/* Speaker */}
          <line x1="37" y1="18" x2="43" y2="18" stroke="#ffe0db" strokeWidth="1" strokeLinecap="round" />
          
          {/* Pulsing play button */}
          <g className="play-btn">
            <circle cx="40" cy="40" r="7" fill="url(#primaryOrange)" />
            <polygon points="38.5,37 43.5,40 38.5,43" fill="white" />
          </g>

          {/* Micro elements at bottom of phone screen */}
          <rect x="33" y="52" width="14" height="2" rx="0.5" fill="#ffd8d3" />
        </g>
      </svg>
    </div>
  );
}

function AnimatedMapPin() {
  return (
    <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
      <svg viewBox="0 0 80 80" className="w-16 h-16">
        <style>{`
          @keyframes bouncePin {
            0%, 100% { transform: translateY(0) scaleY(1); }
            50% { transform: translateY(-5px) scaleY(0.96); }
          }
          @keyframes sonarWave {
            0% { transform: scale(0.4); opacity: 0.8; }
            100% { transform: scale(1.4); opacity: 0; }
          }
          .map-pin {
            transform-origin: 40px 52px;
            animation: bouncePin 2s infinite ease-in-out;
          }
          .sonar-ring {
            transform-origin: 40px 52px;
            animation: sonarWave 2s infinite ease-out;
          }
        `}</style>
        <defs>
          <linearGradient id="mapPinGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ff826e" />
            <stop offset="100%" stopColor="hsl(var(--primary))" />
          </linearGradient>
        </defs>

        {/* Map sonar rings */}
        <ellipse cx="40" cy="52" rx="14" ry="4" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" className="sonar-ring" style={{ animationDelay: "0s" }} />
        <ellipse cx="40" cy="52" rx="14" ry="4" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" className="sonar-ring" style={{ animationDelay: "1s" }} />
        <ellipse cx="40" cy="52" rx="5" ry="1.5" fill="hsl(var(--primary))" opacity="0.3" />

        {/* Folded Map Base */}
        <path d="M 22,46 L 34,42 L 46,46 L 58,42 L 58,62 L 46,66 L 34,62 L 22,66 Z" fill="white" stroke="#ffe0db" strokeWidth="1.5" strokeLinejoin="round" />
        {/* Map roads/routes */}
        <path d="M 25,56 L 34,52 L 46,56 L 55,52" fill="none" stroke="#ffd8d3" strokeWidth="2" strokeLinecap="round" />
        <path d="M 34,42 L 34,62" fill="none" stroke="#ffd8d3" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M 46,46 L 46,66" fill="none" stroke="#ffd8d3" strokeWidth="1.5" strokeLinecap="round" />

        {/* Map Pin */}
        <g className="map-pin">
          <path d="M 40,52 C 32,44 26,37 26,27 A 14,14 0 0 1 54,27 C 54,37 48,44 40,52 Z" fill="url(#mapPinGrad)" stroke="#c33015" strokeWidth="1" strokeLinejoin="round" />
          <circle cx="40" cy="27" r="5" fill="white" />
        </g>
      </svg>
    </div>
  );
}

function AnimatedSupport() {
  return (
    <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
      <svg viewBox="0 0 80 80" className="w-16 h-16">
        <style>{`
          @keyframes bubbleLeft {
            0%, 5% { transform: scale(0); opacity: 0; }
            10%, 45% { transform: scale(1); opacity: 1; }
            50%, 100% { transform: scale(0); opacity: 0; }
          }
          @keyframes bubbleRight {
            0%, 50% { transform: scale(0); opacity: 0; }
            55%, 90% { transform: scale(1); opacity: 1; }
            95%, 100% { transform: scale(0); opacity: 0; }
          }
          @keyframes bounceDot {
            0%, 100% { transform: translateY(0); opacity: 0.3; }
            50% { transform: translateY(-2px); opacity: 1; }
          }
          .bubble-l {
            transform-origin: 22px 50px;
            animation: bubbleLeft 6s infinite cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }
          .bubble-r {
            transform-origin: 58px 38px;
            animation: bubbleRight 6s infinite cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }
          .dot-1 { animation: bounceDot 1.2s infinite ease-in-out; }
          .dot-2 { animation: bounceDot 1.2s infinite ease-in-out 0.2s; }
          .dot-3 { animation: bounceDot 1.2s infinite ease-in-out 0.4s; }
        `}</style>
        <defs>
          <linearGradient id="supportOrangeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="100%" stopColor="#ff826e" />
          </linearGradient>
        </defs>

        {/* Background rings */}
        <circle cx="40" cy="40" r="30" stroke="#ffebe8" strokeWidth="1" strokeDasharray="3 6" fill="none" />

        {/* Left Chat Bubble */}
        <g className="bubble-l">
          <path d="M 20,50 L 20,44 A 6,6 0 0 1 26,38 L 44,38 A 6,6 0 0 1 50,44 L 50,50 A 6,6 0 0 1 44,56 L 24,56 L 18,60 Z" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" />
          <circle cx="28" cy="47" r="1.5" fill="hsl(var(--primary))" className="dot-1" />
          <circle cx="34" cy="47" r="1.5" fill="hsl(var(--primary))" className="dot-2" />
          <circle cx="40" cy="47" r="1.5" fill="hsl(var(--primary))" className="dot-3" />
        </g>

        {/* Right Chat Bubble */}
        <g className="bubble-r">
          <path d="M 36,26 L 54,26 A 6,6 0 0 1 60,32 L 60,38 A 6,6 0 0 1 54,44 L 36,44 A 6,6 0 0 1 30,38 L 30,32 A 6,6 0 0 1 36,26 Z" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" />
          <path d="M 54,44 L 62,48 L 58,40 Z" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" />
          <text x="45" y="37" fill="hsl(var(--primary))" fontSize="9" fontWeight="bold" fontFamily="system-ui, sans-serif" textAnchor="middle">?</text>
        </g>
      </svg>
    </div>
  );
}

function StepConfirmacao({ buyerData, onRestart }: { buyerData: BuyerData; onRestart: () => void }) {
  const [copied, setCopied] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"pix" | "boleto" | "credit_card" | "free">("pix");
  const ORDER = "A247119D";

  useEffect(() => {
    const duration = 5 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const randomInRange = (min: number, max: number) =>
      Math.random() * (max - min) + min;

    const interval = window.setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  const mainContent = (
    <div className="flex flex-col gap-6">
      {/* Visualizer Selector for Homologation */}
      <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 flex flex-col gap-2.5">
        <span className="text-xs font-semibold text-primary uppercase tracking-wider">Homologação Visual: Forma de Pagamento</span>
        <div className="flex flex-wrap gap-2">
          {(["pix", "boleto", "credit_card", "free"] as const).map((method) => (
            <button
              key={method}
              onClick={() => setPaymentMethod(method)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer border transition-all ${
                paymentMethod === method
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-foreground border-border hover:bg-neutral-50"
              }`}
            >
              {method === "pix" && "Pix"}
              {method === "boleto" && "Boleto Bancário"}
              {method === "credit_card" && "Cartão de Crédito"}
              {method === "free" && "Cortesia/Gratuito"}
            </button>
          ))}
        </div>
      </div>

      {/* 1 ─ Hero */}
      <div className="rounded-2xl overflow-hidden border border-border shadow-sm">
        <div className="relative h-56">
          <img src={MOCK_EVENT.coverImage} alt={MOCK_EVENT.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          
          {/* Confirmed badge */}
          <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white text-primary text-[11px] font-medium px-3 py-1.5 rounded-full shadow-sm">
            <Check className="h-3.5 w-3.5 stroke-[2.5] text-primary" /> Pedido confirmado
          </div>

          {/* Acessar transmissão button inside banner */}
          <div className="absolute top-4 right-4">
            <button className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-white text-primary font-medium text-xs border-none cursor-pointer shadow-sm hover:bg-neutral-50 transition-colors">
              <Video className="h-3.5 w-3.5" /> Acessar transmissão
            </button>
          </div>

          {/* Event info inside banner */}
          <div className="absolute bottom-0 left-0 right-0 px-6 py-5 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="space-y-1.5">
              <h2 className="font-display text-white font-semibold leading-tight m-0 text-xl md:text-2xl tracking-tight">
                {MOCK_EVENT.title}
              </h2>
              <div className="flex flex-wrap gap-4 text-white/80 text-xs">
                <span className="flex items-center gap-1.5 font-normal">
                  <Calendar className="h-3.5 w-3.5 shrink-0" /> 02 de junho de 2026
                </span>
                <span className="flex items-center gap-1.5 font-normal">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  15:00 — 19:00
                </span>
                <span className="flex items-center gap-1.5 font-normal bg-white/10 px-2 py-0.5 rounded text-[11px]">
                  Evento Online
                </span>
              </div>
            </div>

            <button 
              className="flex items-center justify-center gap-2 h-10 px-5 rounded-xl text-white font-semibold text-xs cursor-pointer border-none shadow-md transition-all whitespace-nowrap shrink-0 hover:opacity-95"
              style={{ background: "#f04e30" }}
            >
              <Video className="h-3.5 w-3.5" /> Acessar transmissão
            </button>
          </div>
        </div>
      </div>

      {/* Boleto alert box — orange branding style */}
      {paymentMethod === "boleto" && (
        <div className="p-5 rounded-2xl border border-primary/30 bg-primary/5 space-y-4 text-left shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">Boleto ainda não foi pago</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Seus ingressos já foram liberados, mas você precisa pagar o boleto em até <strong>30 dias</strong>. Caso contrário, seu acesso ao evento poderá ser cancelado.
              </p>
            </div>
          </div>

          <button 
            onClick={() => window.open("https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf", "_blank")}
            style={{ background: "#f04e30" }}
            className="w-full h-11 rounded-xl text-white font-semibold text-xs cursor-pointer border-none shadow-sm hover:opacity-95 transition-opacity flex items-center justify-center gap-1.5"
          >
            <ExternalLink className="h-4 w-4" /> Pagar boleto agora (PDF)
          </button>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">Código de barras</p>
            <div className="flex items-center gap-2">
              <input
                value="34191.79001 01043.513184 91020.150008 7 98760000016500"
                readOnly
                className="flex-1 px-3 py-2 rounded-md border border-primary/20 bg-white dark:bg-background text-xs font-mono focus:outline-none"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText("34191.79001 01043.513184 91020.150008 7 98760000016500");
                  toast({ title: "Código de barras copiado!" });
                }}
                className="h-9 px-3 rounded-lg border border-primary/20 bg-white hover:bg-neutral-50 flex items-center justify-center cursor-pointer text-xs"
              >
                <IconCopy />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pix payment box */}
      {paymentMethod === "pix" && (
        <div className="p-5 rounded-2xl border border-primary/20 bg-primary/5 space-y-4 text-left shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <QrCode className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">Pagamento via Pix pendente</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Escaneie o QR Code abaixo ou copie a chave Pix para finalizar a compra do seu ingresso.
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center gap-3 bg-white p-5 rounded-2xl border border-border/60">
            <div className="w-40 h-40 bg-neutral-50 rounded-xl flex items-center justify-center border border-border/40 p-2">
              <QRMock size={140} />
            </div>
            <span className="text-[11px] font-medium text-muted-foreground">Valor: R$ 165,00 · Válido por 15 minutos</span>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">Chave Pix (Copia e Cola)</p>
            <div className="flex items-center gap-2">
              <input
                value="00020101021226870014br.gov.bcb.pix2565pix.tickethall.com.br/qr/v2/cobv/96AF5A065204000053039865406165.005802BR5925TicketHall"
                readOnly
                className="flex-1 px-3 py-2 rounded-md border border-primary/20 bg-white dark:bg-background text-xs font-mono focus:outline-none"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText("00020101021226870014br.gov.bcb.pix2565pix.tickethall.com.br/qr/v2/cobv/96AF5A065204000053039865406165.005802BR5925TicketHall");
                  toast({ title: "Chave Pix copiada!" });
                }}
                className="h-9 px-3 rounded-lg border border-primary/20 bg-white hover:bg-neutral-50 flex items-center justify-center cursor-pointer text-xs"
              >
                <IconCopy />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2 ─ Ticket (boarding-pass style) */}
      <div className="rounded-2xl border border-border bg-card flex overflow-hidden shadow-sm">
        {/* Left: QR code & code info */}
        <div className="flex-none flex flex-col items-center justify-center gap-1 px-6 py-6 bg-white dark:bg-card border-r-0">
          <QRMock size={130} />
          <span className="text-[11px] font-medium text-foreground tracking-wide tabular-nums mt-1.5">
            #FC756FF2
          </span>
          <span className="text-[9px] text-muted-foreground tabular-nums">
            Pedido {ORDER}
          </span>
          <span className="text-[8px] text-muted-foreground tabular-nums">
            19 de jun. de 2026, 09:47
          </span>
        </div>

        {/* Dashed tear line */}
        <div className="relative flex-none" style={{ width: 1 }}>
          <div className="absolute inset-0 border-l border-dashed border-border" />
          <div className="absolute rounded-full bg-background border border-border" style={{ width: 16, height: 16, top: -8, left: -8 }} />
          <div className="absolute rounded-full bg-background border border-border" style={{ width: 16, height: 16, bottom: -8, left: -8 }} />
        </div>

        {/* Right: details */}
        <div className="flex-1 min-w-0 flex flex-col justify-between p-6">
          {/* Header strip */}
          <div className="flex items-center gap-2 mb-3">
            <Ticket className="h-4 w-4 text-primary shrink-0" />
            <span className="text-[10px] font-medium text-muted-foreground">
              Seu ingresso
            </span>
            <span className="ml-1 text-[9px] font-medium px-2 py-0.5 rounded bg-orange-50 text-primary">
              Normal {paymentMethod !== "free" && "— R$ 150,00"}
            </span>
          </div>

          {/* Ticket fields */}
          <div className="flex-1 flex flex-col justify-center gap-4">
            <div>
              <h3 className="font-semibold text-lg leading-tight text-foreground m-0">
                {MOCK_EVENT.title}
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3.5">
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5 shrink-0" />
                  <span>02 de junho de 2026</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span>15:00 – 19:00 (horário de Brasília)</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Video className="h-3.5 w-3.5 shrink-0" />
                  <span>Evento Online</span>
                </div>
              </div>

              <div className="flex flex-col justify-start">
                <span className="text-[10px] font-normal text-muted-foreground mb-0.5">
                  Participante
                </span>
                <span className="text-sm font-medium text-foreground">
                  Davi Nascimento
                </span>
                <span className="text-[11px] text-muted-foreground">
                  090.552.349-07
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2.5 ─ Dados do Pagador & Detalhes da Compra */}
      <div className="rounded-2xl border border-border bg-white dark:bg-card p-6 text-left space-y-4 shadow-sm">
        <h3 className="font-display text-sm font-semibold text-foreground flex items-center gap-1.5 pb-2 border-b border-border m-0">
          <User className="h-4 w-4 text-primary" /> Informações de Faturamento & Pagador
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
          {/* Col 1: Dados do Pagador */}
          <div className="space-y-2.5">
            <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">Dados do Pagador</span>
            <div className="space-y-1.5 text-foreground">
              {buyerData.companyName ? (
                <div>
                  <span className="text-muted-foreground font-normal">Razão Social:</span> <span className="font-semibold">{buyerData.companyName}</span>
                </div>
              ) : (
                <div>
                  <span className="text-muted-foreground font-normal">Nome:</span> <span className="font-semibold">{buyerData.fullName}</span>
                </div>
              )}
              <div>
                <span className="text-muted-foreground font-normal">CPF/CNPJ:</span> <span className="font-semibold font-mono">{buyerData.cpf}</span>
              </div>
              <div>
                <span className="text-muted-foreground font-normal">E-mail:</span> <span className="font-semibold">{buyerData.email}</span>
              </div>
              <div>
                <span className="text-muted-foreground font-normal">Telefone:</span> <span className="font-semibold font-mono">{buyerData.phone}</span>
              </div>
              <div>
                <span className="text-muted-foreground font-normal">Endereço de cobrança:</span>{" "}
                <span className="font-semibold leading-relaxed">
                  {buyerData.street}, {buyerData.addressNumber}
                  {buyerData.complement && ` - ${buyerData.complement}`}
                  , {buyerData.neighborhood} - {buyerData.city}/{buyerData.state} - CEP {buyerData.cep}
                </span>
              </div>
            </div>
          </div>

          {/* Col 2: Detalhes do Pagamento */}
          <div className="space-y-2.5">
            <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">Detalhes do Pagamento</span>
            <div className="space-y-1.5 text-foreground">
              <div>
                <span className="text-muted-foreground font-normal">Forma de pagamento:</span>{" "}
                <span className="font-semibold">
                  {paymentMethod === "pix" && "Pix"}
                  {paymentMethod === "boleto" && "Boleto Bancário"}
                  {paymentMethod === "credit_card" && "Cartão de Crédito"}
                  {paymentMethod === "free" && "Cortesia / Gratuito"}
                </span>
              </div>
              {paymentMethod === "credit_card" && (
                <>
                  <div>
                    <span className="text-muted-foreground font-normal">Cartão:</span> <span className="font-semibold font-mono">Visa final 4242</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground font-normal">Parcelamento:</span> <span className="font-semibold">1x de R$ 165,00 (sem juros)</span>
                  </div>
                </>
              )}
              {paymentMethod === "boleto" && buyerData.companyName && (
                <div>
                  <span className="text-muted-foreground font-normal">Boleto emitido para CNPJ:</span> <span className="font-semibold font-mono">{buyerData.cpf}</span>
                </div>
              )}
              <div>
                <span className="text-muted-foreground font-normal">Status do pagamento:</span>{" "}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  paymentMethod === "free" || paymentMethod === "credit_card"
                    ? "bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400"
                    : "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary"
                }`}>
                  {paymentMethod === "free" || paymentMethod === "credit_card" ? "Aprovado" : "Aguardando pagamento"}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground font-normal">Total da compra:</span>{" "}
                <span className="font-semibold text-primary">
                  {paymentMethod === "free" ? "Gratuito" : "R$ 165,00"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3 ─ Actions Side-by-Side */}
      <div className="flex flex-row gap-4">
        <button 
          className="flex items-center justify-center gap-2 h-11 flex-1 rounded-xl text-white font-medium text-xs cursor-pointer hover:opacity-90 transition-opacity border-none"
          style={{ background: "#f04e30" }}
        >
          <Download className="h-3.5 w-3.5" /> Baixar ingresso (PDF)
        </button>
        <button 
          className="flex items-center justify-center gap-2 h-11 flex-1 rounded-xl border border-border bg-white text-foreground font-medium text-xs cursor-pointer hover:bg-muted/50 transition-colors"
        >
          <Calendar className="h-3.5 w-3.5" /> Adicionar ao calendário
        </button>
      </div>

      {/* 4 ─ Informações importantes (3 clean white cards) */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 px-1">
          <HelpCircle className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Informações importantes
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Card 1: Ingressos enviados */}
          <div className="flex flex-col items-center text-center p-6 rounded-2xl bg-white dark:bg-card border border-border/60 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-md transition-all duration-300">
            <AnimatedPaperPlane />
            <div className="mt-4 space-y-1">
              <h4 className="text-sm font-semibold text-foreground">Ingressos enviados</h4>
              <p className="text-xs leading-relaxed text-muted-foreground m-0">
                Os ingressos foram enviados para <strong className="font-semibold text-foreground">{buyerData.email}</strong>
              </p>
            </div>
          </div>

          {/* Card 2: Link da transmissão ou Local do evento */}
          {MOCK_EVENT.venue.toLowerCase().includes("online") ? (
            <div className="flex flex-col items-center text-center p-6 rounded-2xl bg-white dark:bg-card border border-border/60 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-md transition-all duration-300">
              <AnimatedTransmission />
              <div className="mt-4 space-y-1">
                <h4 className="text-sm font-semibold text-foreground">Link da transmissão</h4>
                <p className="text-xs leading-relaxed text-muted-foreground m-0">
                  O link da transmissão será disponibilizado no horário do evento.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center text-center p-6 rounded-2xl bg-white dark:bg-card border border-border/60 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-md transition-all duration-300">
              <AnimatedMapPin />
              <div className="mt-4 space-y-1">
                <h4 className="text-sm font-semibold text-foreground">Local do evento</h4>
                <p className="text-xs leading-relaxed text-muted-foreground m-0">
                  Realizado em <strong className="font-semibold text-foreground">{MOCK_EVENT.venue}</strong>. Verifique o endereço no seu ingresso.
                </p>
              </div>
            </div>
          )}

          {/* Card 3: Precisa de ajuda? */}
          <div className="flex flex-col items-center text-center p-6 rounded-2xl bg-white dark:bg-card border border-border/60 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-md transition-all duration-300">
            <AnimatedSupport />
            <div className="mt-4 space-y-1">
              <h4 className="text-sm font-semibold text-foreground">Precisa de ajuda?</h4>
              <p className="text-xs leading-relaxed text-muted-foreground m-0">
                Acesse nossa <a href="#" className="text-primary font-semibold hover:underline">Central de Ajuda</a> ou entre em contato.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 5 ─ Final text and main CTAs */}
      <div className="text-center pt-2 space-y-4">
        <p className="text-xs text-muted-foreground leading-relaxed max-w-md mx-auto m-0">
          Seus ingressos foram gerados com sucesso. Você pode acessá-los na seção "Meus Ingressos".
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button 
            className="flex items-center justify-center h-10 px-6 rounded-lg text-white font-medium text-xs cursor-pointer border-none hover:opacity-95 transition-opacity"
            style={{ background: "#f04e30" }}
          >
            Ver meus ingressos
          </button>
          <button 
            className="flex items-center justify-center h-10 px-6 rounded-lg border border-primary bg-white text-primary font-medium text-xs cursor-pointer hover:bg-orange-50/35 transition-colors"
          >
            Explorar mais eventos
          </button>
        </div>
      </div>
    </div>
  );

  const sidebar = (
    <aside className="w-full lg:w-[300px] xl:w-[320px] shrink-0">
      <div className="rounded-2xl border border-border bg-card p-6 flex flex-col gap-5 sticky top-6 shadow-sm">
        <h3 className="font-display text-sm font-semibold text-foreground m-0">
          Resumo do pedido
        </h3>

        {/* Order number */}
        <div className="flex flex-col gap-1">
          <p className="text-[10px] font-normal text-muted-foreground m-0">Número do pedido</p>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-base tabular-nums text-foreground">#{ORDER}</span>
            <button
              onClick={() => { navigator.clipboard?.writeText(ORDER); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="p-1 rounded text-muted-foreground hover:text-foreground bg-transparent border-none cursor-pointer transition-colors"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

        <div className="border-t border-border" />

        {/* Status */}
        <div className="flex flex-col gap-1">
          <p className="text-[10px] font-normal text-muted-foreground m-0">Status</p>
          <div className="flex items-center gap-1.5 text-primary font-medium text-sm">
            <div className="w-4 h-4 rounded-full bg-orange-50 flex items-center justify-center text-primary">
              <Check className="w-2.5 h-2.5 stroke-[2.5]" />
            </div>
            {paymentMethod === "free" || paymentMethod === "credit_card" ? "Confirmado" : "Aguardando pagamento"}
          </div>
        </div>

        {/* Participante */}
        <div className="flex flex-col gap-1">
          <p className="text-[10px] font-normal text-muted-foreground m-0">Participante</p>
          <p className="font-normal text-sm text-foreground m-0">Davi Nascimento</p>
        </div>

        {/* E-mail */}
        <div className="flex flex-col gap-1">
          <p className="text-[10px] font-normal text-muted-foreground m-0">E-mail</p>
          <p className="font-normal text-sm text-foreground m-0">{buyerData.email}</p>
        </div>

        <div className="border-t border-border" />

        {/* Total */}
        <div className="flex flex-col gap-1">
          <p className="text-[10px] font-normal text-muted-foreground m-0">Total pago</p>
          <p className="font-semibold text-lg m-0 text-primary">
            {paymentMethod === "free" ? "Gratuito" : "R$ 165,00"}
          </p>
          <p className="text-[11px] text-muted-foreground m-0">
            {paymentMethod === "free" && "Liberado em 02/06/2026"}
            {paymentMethod === "credit_card" && "Pago em 02/06/2026 via Cartão"}
            {paymentMethod === "pix" && "Aguardando pagamento via Pix"}
            {paymentMethod === "boleto" && "Aguardando pagamento via Boleto"}
          </p>
        </div>

        {/* Security badge at bottom of sidebar */}
        <div className="rounded-xl p-3.5 flex gap-2.5 items-start border border-orange-100 bg-orange-50/20">
          <div className="text-primary shrink-0 mt-0.5">
            <Shield className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[11px] font-semibold text-orange-800 m-0 mb-0.5">Ambiente seguro</p>
            <p className="text-[10px] leading-relaxed text-orange-700/80 m-0">
              Seus dados estão protegidos com criptografia e não são compartilhados.
            </p>
          </div>
        </div>

        <button onClick={onRestart} className="text-xs text-center cursor-pointer bg-transparent border-none underline text-muted-foreground hover:text-foreground mt-2">
          Reiniciar preview
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-8 items-start">
      <div className="flex-1 min-w-0">{mainContent}</div>
      {sidebar}
    </div>
  );
}

// ----- Root -----
export default function CheckoutPreview() {
  const [step, setStep] = useState(0);
  const [buyerData, setBuyerData] = useState<BuyerData>(MOCK_BUYER);

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="bg-primary/15 border-b border-primary/30 text-primary text-xs font-semibold text-center py-1.5 tracking-wide">
        ⚠ PREVIEW TEMPORÁRIO — não commitar este arquivo
      </div>

      {step <= 2 && (
        <div className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur shadow-sm">
          <div className="container max-w-5xl mx-auto py-4 px-4">
            <div className="flex items-center justify-between gap-4">
              <Link to="#" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground shrink-0">
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Voltar ao carrinho</span>
              </Link>
              <div className="flex items-center gap-0 flex-1 justify-center max-w-sm mx-auto">
                {MOCK_STEPS.map((s, i) => {
                  const isCompleted = i < step;
                  const isActive = i === step;
                  return (
                    <div key={s} className="flex items-center flex-1 last:flex-none">
                      <div className="flex flex-col items-center gap-1">
                        <button
                          onClick={() => setStep(i)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 border-2 cursor-pointer ${
                            isCompleted ? "bg-primary border-primary text-primary-foreground" :
                            isActive ? "bg-primary border-primary text-primary-foreground" :
                            "bg-background border-border text-muted-foreground"
                          }`}
                        >
                          {isCompleted ? <Check className="h-3.5 w-3.5 stroke-[3]" /> : i + 1}
                        </button>
                        <span className={`text-[10px] font-medium whitespace-nowrap hidden sm:block ${isActive ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
                      </div>
                      {i < MOCK_STEPS.length - 1 && (
                        <div className={`h-0.5 flex-1 mx-1 mb-4 sm:mb-[18px] transition-colors duration-500 ${i < step ? "bg-primary" : "bg-border"}`} />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="w-20 shrink-0" />
            </div>
          </div>
        </div>
      )}

      {step < 2 ? (
        <div className="container max-w-5xl mx-auto pt-8 px-4">
          <div className="flex flex-col lg:flex-row gap-8 justify-center">
            <div className="flex-1 max-w-2xl" style={{ zoom: 1.15 }}>
              {step === 0 && <StepComprador buyerData={buyerData} setBuyerData={setBuyerData} onNext={() => setStep(1)} />}
              {step === 1 && <StepParticipantes onNext={() => setStep(2)} onBack={() => setStep(0)} />}
            </div>
            <Sidebar />
          </div>
        </div>
      ) : (
        <div className="container max-w-6xl mx-auto pt-8 px-4">
          <StepConfirmacao buyerData={buyerData} onRestart={() => setStep(0)} />
        </div>
      )}
    </div>
  );
}
